import clsx from 'clsx';
import { Icon } from 'common/icon';
import Image from 'next/image';
import { useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';

import styles from './styles.module.css';

import { InputField } from 'common';
import { Rating } from '../casino-review/rating';
import { Button } from 'common/new-button';
import { Formik, FormikValues } from 'formik';
import { useAlert } from 'components';
import { useCreateStreamerReviewsMutation } from 'hooks/api/streamer_reviews';
import { useMeQuery } from 'hooks/api/users';
import { handleMutation } from 'utils/handleMutation';
import { initialValues, model, validationSchema } from './config';

import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';

import { IStreamerReview, IStreamer } from 'constants/types';
import { useClickOutside } from 'hooks/useClickOutside';
import { Routes } from 'constants/routes';

type Props = {
  streamer: IStreamer;
  isVisible: boolean;
  onSuccess?: () => void;
  setVisible: (isVisible: boolean) => void;
};

export const StreamerReviewModal = ({
  isVisible,
  setVisible,
  streamer,
  onSuccess,
}: Props) => {
  const alert = useAlert();
  const router = useRouter();

  const modalRef = useRef<HTMLDivElement | null>(null);

  const { data: me } = useMeQuery();

  const [placeReview, mutation] = useCreateStreamerReviewsMutation();
  const isLoading = mutation.isLoading;

  const initialReviewValues = useMemo(() => {
    if (me?.user?.streamers_reviews && me.user.streamers_reviews.length > 0) {
      const current = me?.user?.streamers_reviews.find(
        (review: IStreamerReview) => review.streamer_id === streamer._id
      );
      if (current) {
        return current;
      }
    }
    return {};
  }, [me?.user?.streamers_reviews, streamer._id]);

  const onReviewAdded = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }
  }, [onSuccess]);

  handleMutation({ ...mutation, onSuccess: onReviewAdded });

  const onSubmit = async (values: FormikValues) => {
    const { review, rating } = values;
    if (!rating) {
      alert.error('Please select rating');
      return;
    }

    if (!me?.user?._id) {
      router.push(Routes.login);
      return;
    }

    await placeReview({
      streamerId: streamer._id.toString(),
      review,
      rating,
    });

    alert.success('Review successfully added');
    setVisible(false);
  };

  const handleCloseModal = () => {
    setVisible(false);
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <div className={styles.container}>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues(initialReviewValues)}
          validationSchema={validationSchema}
        >
          {({ dirty, isSubmitting, setFieldValue, submitForm, values }) => (
            <div className={styles.wrapper} ref={modalRef}>
              <div className={styles.top}>
                <div className={styles.topLeft}>
                  <div className={styles.image}>
                    <LogoOverlayWrapper>
                      {streamer?.logoUrl && (
                        <LogoOverlay
                          width={60}
                          height={60}
                          alt={streamer?.username}
                          image={streamer?.logoUrl}
                        />
                      )}
                      {streamer?.imageUrl && (
                        <Image
                          src={streamer?.imageUrl}
                          alt="review-image"
                          width={60}
                          height={60}
                        />
                      )}
                    </LogoOverlayWrapper>
                  </div>

                  <div className={styles.text}>
                    <span>{streamer?.username}</span>
                  </div>
                </div>

                <div
                  className={styles.closeButton}
                  onClick={() => setVisible(false)}
                >
                  <Icon.Close />
                </div>
              </div>

              <div className={styles.rating}>
                <Rating
                  variant="streamer"
                  value={values.rating || 0}
                  onChange={(rating) => setFieldValue('rating', rating)}
                />
              </div>
              <div className={styles.multiTextInput}>
                <InputField {...model.review} />
              </div>
              <div className={styles.submitButton}>
                <Button
                  text={isLoading ? 'Loading...' : 'Send review'}
                  onClick={submitForm}
                  disabled={!dirty || isSubmitting}
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};
